/**
 * BrainSwipe Store (Zustand)
 */

import { create } from 'zustand';
import type { SwipeQuestion, SwipeSession, AnswerResult, CategoryInfo, SwipeScreen } from '../types/swipe';
import { fetchNextQuestions, submitAnswer, fetchCategories, fetchTags, resetSession } from '../lib/swipe-api';
import type { TagInfo } from '../lib/swipe-api';

interface SwipeState {
  // Screen
  screen: SwipeScreen;

  // Categories
  categories: CategoryInfo[];
  selectedCategory: string | null;
  
  // Tags
  tags: TagInfo[];
  selectedTag: string | null;
  
  // Other filters
  quizIds: string[] | null;      // Фильтр по ID викторин

  // Questions
  questions: SwipeQuestion[];
  currentIndex: number;
  answeredIds: Set<string>;

  // Current answer feedback
  lastAnswer: AnswerResult | null;
  selectedAnswerIndex: number | null;
  answerStartTime: number | null;

  // Session
  session: SwipeSession | null;

  // Loading states
  isLoading: boolean;
  isLoadingTags: boolean;
  isAnswering: boolean;
  error: string | null;

  // Actions
  loadCategories: () => Promise<void>;
  loadTags: () => Promise<void>;
  selectCategory: (categoryId: string | null) => void;
  selectTag: (tag: string | null) => void;
  startGame: () => Promise<void>;
  loadMoreQuestions: () => Promise<void>;
  selectAnswer: (index: number) => void;
  submitCurrentAnswer: () => Promise<void>;
  nextQuestion: () => void;
  endGame: () => void;
  reset: () => Promise<void>;
  setFilters: (quizIds: string[] | null, tag: string | null) => void;
}

const PRELOAD_COUNT = 5;
const PRELOAD_THRESHOLD = 2; // Load more when 2 questions left

export const useSwipeStore = create<SwipeState>((set, get) => ({
  // Initial state
  screen: 'start',
  categories: [],
  selectedCategory: null,
  tags: [],
  selectedTag: null,
  quizIds: null,
  questions: [],
  currentIndex: 0,
  answeredIds: new Set(),
  lastAnswer: null,
  selectedAnswerIndex: null,
  answerStartTime: null,
  session: null,
  isLoading: false,
  isLoadingTags: false,
  isAnswering: false,
  error: null,

  // Actions
  loadCategories: async () => {
    set({ isLoading: true, error: null });
    try {
      const categories = await fetchCategories();
      set({ categories, isLoading: false });
    } catch (err) {
      set({ error: 'Не удалось загрузить категории', isLoading: false });
    }
  },

  loadTags: async () => {
    set({ isLoadingTags: true });
    try {
      const tags = await fetchTags();
      set({ tags, isLoadingTags: false });
    } catch (err) {
      console.error('Failed to load tags:', err);
      set({ isLoadingTags: false });
    }
  },

  selectCategory: (categoryId) => {
    set({ selectedCategory: categoryId });
  },

  selectTag: (tag) => {
    set({ selectedTag: tag });
  },

  startGame: async () => {
    const { selectedCategory, quizIds, selectedTag } = get();
    set({ isLoading: true, error: null });

    try {
      const result = await fetchNextQuestions({
        count: PRELOAD_COUNT,
        category: selectedCategory || undefined,
        quizIds: quizIds || undefined,
        tag: selectedTag || undefined,
      });

      set({
        screen: 'playing',
        questions: result.questions,
        currentIndex: 0,
        session: result.session,
        answeredIds: new Set(),
        answerStartTime: Date.now(),
        isLoading: false,
      });
    } catch (err) {
      set({ error: 'Не удалось загрузить вопросы', isLoading: false });
    }
  },

  loadMoreQuestions: async () => {
    const { selectedCategory, quizIds, selectedTag, answeredIds, questions } = get();

    try {
      const result = await fetchNextQuestions({
        count: PRELOAD_COUNT,
        category: selectedCategory || undefined,
        quizIds: quizIds || undefined,
        tag: selectedTag || undefined,
        excludeIds: [...answeredIds, ...questions.map(q => q.id)],
      });

      if (result.questions.length > 0) {
        set(state => ({
          questions: [...state.questions, ...result.questions],
        }));
      }
    } catch (err) {
      console.error('Failed to load more questions:', err);
    }
  },

  selectAnswer: (index) => {
    const { isAnswering, lastAnswer } = get();
    if (isAnswering || lastAnswer) return; // Already answering or showing feedback

    set({ selectedAnswerIndex: index });
    get().submitCurrentAnswer();
  },

  submitCurrentAnswer: async () => {
    const { questions, currentIndex, selectedAnswerIndex, answerStartTime } = get();

    if (selectedAnswerIndex === null) return;

    const question = questions[currentIndex];
    if (!question) return;

    const timeSpent = answerStartTime ? Date.now() - answerStartTime : 0;

    set({ isAnswering: true });

    try {
      const result = await submitAnswer(question.id, selectedAnswerIndex, timeSpent);

      set(state => ({
        lastAnswer: result,
        screen: 'feedback',
        session: {
          id: state.session?.id || '',
          ...result.session,
          currentStreak: result.currentStreak,
        },
        answeredIds: new Set([...state.answeredIds, question.id]),
        isAnswering: false,
      }));

      // Preload more if running low
      const remainingQuestions = questions.length - currentIndex - 1;
      if (remainingQuestions <= PRELOAD_THRESHOLD) {
        get().loadMoreQuestions();
      }
    } catch (err) {
      set({ error: 'Ошибка отправки ответа', isAnswering: false });
    }
  },

  nextQuestion: () => {
    const { questions, currentIndex } = get();
    const nextIndex = currentIndex + 1;

    if (nextIndex >= questions.length) {
      // No more questions
      set({ screen: 'finished' });
    } else {
      set({
        currentIndex: nextIndex,
        screen: 'playing',
        lastAnswer: null,
        selectedAnswerIndex: null,
        answerStartTime: Date.now(),
      });
    }
  },

  endGame: () => {
    set({ screen: 'finished' });
  },

  reset: async () => {
    try {
      await resetSession();
    } catch (err) {
      console.error('Failed to reset session:', err);
    }

    set({
      screen: 'start',
      questions: [],
      currentIndex: 0,
      answeredIds: new Set(),
      lastAnswer: null,
      selectedAnswerIndex: null,
      answerStartTime: null,
      session: null,
      error: null,
      selectedTag: null,
    });
  },

  setFilters: (quizIds, tag) => {
    set({ quizIds, selectedTag: tag });
  },
}));